/**
 * @file Pied de page de branding Brydge, discret, partagé widget et site public.
 * @module packages.common.components.branding-footer.BrandingFooter
 *
 * Affiche un libellé de branding discret (« Réservation simplifiée par Brydge »
 * ou « Propulsé par Brydge » selon le type) avec le logo Brydge et un lien vers
 * le site. Partagé par les widgets embarquables et la page publique de
 * réservation.
 */
import React from 'react';
import PropTypes from 'prop-types';
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const BrandingContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.5, 0, 1, 0),
  marginTop: theme.spacing(1),
}));

const BrandingLink = styled('a')(() => ({
  'display': 'flex',
  'alignItems': 'center',
  'textDecoration': 'none',
  'color': 'inherit',
  'transition': 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.8,
  },
}));

const BrydgeLogo = styled('span')(({theme}) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginLeft: theme.spacing(0.5),
}));

const LogoImage = styled('img')({
  height: 14,
  width: 'auto',
  marginRight: 4,
});

/**
 * Affiche le pied de page de branding Brydge.
 *
 * @param {Object} props
 * @param {string} props.widgetType - Type de contexte ('reservations' ou 'giftvouchers')
 * @param {Function} props.t - Fonction de traduction (libellés par défaut en français si absente)
 * @return {JSX.Element}
 */
export function BrandingFooter({widgetType = 'reservations', t}) {
  const text = widgetType === 'reservations' ?
    (t ? t('widgets.common.reservationSimplifiedBy') : 'Réservation simplifiée par') :
    (t ? t('common.poweredBy') : 'Propulsé par');

  return (
    <BrandingContainer>
      <BrandingLink
        href="https://brydge.app"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${text} Brydge (s'ouvre dans un nouvel onglet)`}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.7rem',
            letterSpacing: '0.02em',
          }}
        >
          <LogoImage src="https://files.brydge.app/assets/logo/logo.svg" alt="Brydge" />
          {text}
          <BrydgeLogo>Brydge</BrydgeLogo>
        </Typography>
      </BrandingLink>
    </BrandingContainer>
  );
}

BrandingFooter.propTypes = {
  widgetType: PropTypes.oneOf(['reservations', 'giftvouchers']),
  t: PropTypes.func,
};

export default BrandingFooter;
